import { NumberService } from '../../../../services/number/number.service';
import { OptionType } from '../../../../domain/trading.domain';

import {
  AcknowledgementStatus,
  ConditionDetails,
  Conditions,
  EquityExtendedTerms,
  EquityOrderAction,
  OptionOrderAction,
  OrderActionParam,
  OrderConditions,
  OrderPriceAmount,
  PriceSelection,
  PriceType
} from '../../domain';
import { FractionalEligibility, Month, OptionSymbolUrlParam } from '../domain/order-entry.domain';

export abstract class OrderEntryUtilityService {
  private static readonly MONTHS: Array<Month> = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  private static readonly actionParamMap: Map<OrderActionParam, EquityOrderAction | OptionOrderAction> =
    new Map<OrderActionParam, EquityOrderAction | OptionOrderAction>([
      [OrderActionParam.BO, OptionOrderAction.BUY_TO_OPEN],
      [OrderActionParam.BC, OptionOrderAction.BUY_TO_CLOSE],
      [OrderActionParam.SOC, OptionOrderAction.SELL_TO_OPEN_COVERED],
      [OrderActionParam.SOU, OptionOrderAction.SELL_TO_OPEN_UNCOVERED],
      [OrderActionParam.SC, OptionOrderAction.SELL_TO_CLOSE],
      [OrderActionParam.buy, EquityOrderAction.BUY],
      [OrderActionParam.sell, EquityOrderAction.SELL]
    ]);

  static setEquityPrice(extendedTerms: Pick<EquityExtendedTerms, 'limitPrice' | 'triggerPrice' | 'trailingStopTerms'>,
                        priceSelection: PriceSelection,
                        priceAmount: OrderPriceAmount): void {
    if (priceSelection === PriceType.LIMIT) {
      extendedTerms.limitPrice = priceAmount.priceLimit;
    } else if (priceSelection === PriceType.STOP_MARKET) {
      extendedTerms.triggerPrice = priceAmount.priceTrigger;
    } else if (priceSelection === PriceType.STOP_LIMIT) {
      extendedTerms.limitPrice = priceAmount.priceLimit;
      extendedTerms.triggerPrice = priceAmount.priceTrigger;
    } else if (priceSelection === PriceType.TRAILING_STOP_MARKET) {
      if (priceAmount.deltaTrigger !== undefined) {
        extendedTerms.trailingStopTerms = { deltaAmount: priceAmount.deltaTrigger };
      } else if (priceAmount.deltaPercent !== undefined) {
        extendedTerms.trailingStopTerms = { deltaPercent: priceAmount.deltaPercent };
      }
    } else if (priceSelection === PriceType.TRAILING_STOP_LIMIT) {
      if (priceAmount.deltaTrigger !== undefined) {
        extendedTerms.trailingStopTerms = {
          deltaAmount: priceAmount.deltaTrigger,
          limitDelta: priceAmount.deltaLimit
        };
      } else if (priceAmount.deltaPercent !== undefined) {
        extendedTerms.trailingStopTerms = {
          deltaPercent: priceAmount.deltaPercent,
          limitDelta: priceAmount.deltaLimit
        };
      }
    } else if (priceSelection === PriceType.MARKET) {
      extendedTerms.limitPrice = extendedTerms.triggerPrice = null;
      if (extendedTerms.trailingStopTerms !== undefined) {
        extendedTerms.trailingStopTerms = {
          deltaAmount: null,
          deltaPercent: null,
          limitDelta: null
        };
      }
    }
  }

  static acknowledgeConditions(orderConditions: Conditions,
                               conditions: Pick<OrderConditions,
                               'acknowledgementRequired' |
                               'regulatoryCustomLogicRequired' |
                               'regulatoryAcknowledgementRequired' |
                               'explicitAcknowledgementRequired'>): void {
    if (conditions.acknowledgementRequired) {
      OrderEntryUtilityService.acknowledge(orderConditions.ACKNOWLEDGEMENTREQUIRED);
    }
    if (conditions.regulatoryCustomLogicRequired) {
      OrderEntryUtilityService.acknowledge(orderConditions.REGULATORYCUSTOMLOGICREQUIRED);
    }
    if (conditions.regulatoryAcknowledgementRequired) {
      OrderEntryUtilityService.acknowledge(orderConditions.REGULATORYACKNOWLEDGEMENTREQUIRED);
    }
    if (conditions.explicitAcknowledgementRequired) {
      OrderEntryUtilityService.acknowledge(orderConditions.EXPLICITACKNOWLEDGEMENTREQUIRED);
    }
  }

  static mapOrderActionParam(orderActionParam: OrderActionParam, quantity: number): OptionOrderAction | EquityOrderAction {
    let actionParam = OrderEntryUtilityService.actionParamMap.get(orderActionParam);
    if (actionParam === EquityOrderAction.BUY || actionParam === EquityOrderAction.SELL) {
      if (quantity < 0) {
        actionParam = actionParam === EquityOrderAction.BUY ? OptionOrderAction.BUY_TO_CLOSE :
          OptionOrderAction.SELL_TO_OPEN_COVERED;
      } else {
        actionParam = actionParam === EquityOrderAction.BUY ? OptionOrderAction.BUY_TO_OPEN :
          OptionOrderAction.SELL_TO_CLOSE;
      }
    }
    return actionParam;
  }

  static parseOptionUrlParams(optionString: string): OptionSymbolUrlParam { // TODO: router - custom parameter parser?
    const values = decodeURI(optionString).toUpperCase().split(' ');
    const expiryDate = OrderEntryUtilityService.decode(OrderEntryUtilityService.parseOptionDateToDate(values[2]));
    return {
      underlyingSymbol: values[0],
      optionType: values[1] === 'C' ? OptionType.CALL : OptionType.PUT,
      expiryDate,
      strikePrice: parseFloat(values[3])
    };
  }

  /**Parses option data coming from URL parameter to OPTION_DATE_FORMAT: yyyy-MM-ddTHH:mm:ss
   * ex: "17JAN20" to 2020-01-17T00:00:00"
   * It counts the years since 2000 and tries its best to handle invalid input
   * @param stringValue date string
   * @return formatted date string
   * */
  static parseOptionDateToDate(stringValue: string): string {
    if (stringValue === undefined || stringValue === null || stringValue.length !== 7) {
      return undefined;
    }

    const day = stringValue.substring(0, 2);

    if (isNaN(Number(day)) || Number(day) < 1 || Number(day) > 31) {
      return undefined;
    }
    const monthIndex = OrderEntryUtilityService.MONTHS.indexOf(stringValue.substring(2, 5) as Month);

    if (monthIndex < 0) {
      return undefined;
    }
    const month = monthIndex + 1;
    const year = '20' + stringValue.substring(5);
    return year + '-' + (month < 10 ? '0' : '') + month + '-' + day + 'T00:00:00';
  }

  /**
   * @param dateString String ISO 8601 date string  the yyyy-mm-ddThh:mm:ss[timezone offset]
   * @returns  Date
   * @description
   *
   * Parses date string and return it in the milliseconds (copy over from DateCodecService
   */
  static decode(dateString: string): Date {
    let dateAndTime: Array<string>;
    let partsDate: Array<string>;
    let partsTime: Array<string>;

    if (typeof dateString === 'string' && (dateString.length === 24 || dateString.length === 19)) {
      dateAndTime = dateString.split('T');
      partsDate = dateAndTime[0].split('-');
      partsTime = dateAndTime[1].substring(0, 8).split(':');
      return new Date(
        parseInt(partsDate[0], 10),
        parseInt(partsDate[1], 10) - 1,
        parseInt(partsDate[2], 10),
        parseInt(partsTime[0], 10),
        parseInt(partsTime[1], 10),
        parseInt(partsTime[2], 10));
    }
    return undefined;
  }

  /**serializes date to option date
   * ex: "2020-01-17T00:00:00" to "17JAN20"
   * @param dateToSerialize Date
   * @return formatted string
   * */
  static serializeDateToOptionDate(dateToSerialize: Date): string {
    const utcDay = dateToSerialize.getUTCDate();
    const day = utcDay >= 10 ? utcDay : '0' + utcDay;
    return day + OrderEntryUtilityService.MONTHS[dateToSerialize.getUTCMonth()]
      + (dateToSerialize.getUTCFullYear() - 2000); // might be a problem after a thousand years
  }

  static serializeOptionSymbol(company: string, optionType: string, expiryDate: Date, priceValue: number): string {
    const type = optionType.charAt(0);
    const date = OrderEntryUtilityService.serializeDateToOptionDate(expiryDate);
    return `${company} ${type} ${date} ${priceValue}`;
  }

  static getId(name: string, index: number): string {
    if (index === undefined) {
      return name;
    }
    return `${name}-${index}`;
  }

  static truncateTill(value: number, points: number): number {
    const fact = Math.pow(10, points);
    return Math.trunc(value * fact) / fact;
  }

  static hasDecimalPoints(value: string, points: number): boolean {
    const index = value.indexOf('.');
    return  index >= 0 && index < value.length - points;
  }

  static truncateQuantityDecimal(orderQuantity: number,
                                 orderAction: EquityOrderAction,
                                 fractionalEligibility: FractionalEligibility): number {
    let processedQuantity = orderQuantity;

    if(NumberService.getPrecision(orderQuantity) > 0) {
      if (fractionalEligibility.fractionalSell === false && orderAction === EquityOrderAction.SELL ||
          fractionalEligibility.fractionalBuy === false && orderAction === EquityOrderAction.BUY) {
        processedQuantity = NumberService.truncateDecimal(orderQuantity, 0);
      }
    }

    return processedQuantity;
  }

  static isValidNumber(amount: number): boolean {
    return NumberService.isNumber(amount);
  }

  private static acknowledge(conditionsList: Array<ConditionDetails>): void {
    if (conditionsList !== undefined) {
      conditionsList.forEach((ack: ConditionDetails): void => {
        ack.acknowledgement = AcknowledgementStatus.USER_ACCEPTED;
      });
    }
  }
}

